import { useState } from "react";
import Banner from "@/components/Banner";
import type { Action } from "@/components/MenuButton";
import ObjectHeading from "@/components/ObjectHeading";
import type { MetaData } from "@/components/ObjectHeading";
import { displayMentionText, parseMentions } from "@/components/AtMention";
import Breadcrumb from "@/components/Breadcrumb";
import { useCanManageTasksBool } from "@/hooks/useCapabilities";
import { toFormat } from "@/lib/dateHelpers";
import useTaskStageActions from "@/pages/tasks/_hooks/useTaskStageActions";
import useUpdateTask from "@/pages/tasks/_hooks/useUpdateTask";
import { getStageSplitAction, getStageTag } from "@/pages/tasks/_utils/taskHelpers";
import { useNavigate } from "@/router";
import { TaskStageValues, TaskVisibilityValues } from "@/types";
import type { CRMTask } from "@/types";
import DeleteTaskDialog from "@/pages/tasks/_components/DeleteTaskDialog";
import EditTaskDialog from "./EditTaskDialog";

interface TaskDetailsHeaderProps {
  task: CRMTask;
  onAddReference?: () => void;
}

export default function TaskDetailsHeader({ task, onAddReference }: TaskDetailsHeaderProps) {
  const stageTag = getStageTag(task);
  const formattedDueDate = task.dueDate ? toFormat(task.dueDate, "MMM dd, yyyy") : null;
  const visibilityText = task.visibility === TaskVisibilityValues.PRIVATE ? "Private" : "Not Private";
  const actions = useTaskStageActions(task.id, { task, source: "task_detail" });
  const { update } = useUpdateTask(task.id, { task, source: "task_detail" });
  const canManageTasks = useCanManageTasksBool();
  const navigate = useNavigate();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const teamNames = (task.teamTags ?? []).map((tag) => tag.name).filter(Boolean);
  const teamsText = teamNames.join(", ");

  const { primary: primaryStageAction, alternatives: stageAlternatives } = getStageSplitAction(task.stage, actions);

  const isComplete = task.stage === TaskStageValues.COMPLETE;
  const canEdit = canManageTasks && !isComplete;
  const openEdit = canEdit ? () => setShowEditDialog(true) : undefined;
  const nameHasMentions = parseMentions(task.name || "").some((segment) => segment.type === "mention");

  const deleteTaskAction: Action = {
    text: "Delete Task",
    iconName: "delete",
    destructive: true,
    onClick: () => setShowDeleteDialog(true),
    testId: "delete-task-button",
    ...(isComplete ? { variant: "text" as const, theme: "error" as const } : {})
  };

  const headerActions: Action[] = [
    ...(primaryStageAction ? [primaryStageAction] : []),
    ...(canEdit
      ? [
          {
            text: "Edit Task",
            onClick: () => setShowEditDialog(true),
            testId: "edit-task-button",
            section: "Manage" as const
          }
        ]
      : []),
    ...stageAlternatives,
    ...(onAddReference
      ? [
          {
            text: "Add Reference",
            onClick: onAddReference,
            testId: "add-reference-action",
            section: "Actions" as const
          }
        ]
      : []),
    ...(canManageTasks ? [deleteTaskAction] : [])
  ];

  const metaData: Exclude<MetaData, string> = [
    ...(formattedDueDate
      ? [
          {
            text: formattedDueDate,
            label: "Due",
            iconName: "event" as const,
            onClick: openEdit
          }
        ]
      : []),
    ...(task.assignedTo?.name
      ? [
          {
            text: task.assignedTo.name,
            label: "Assignee",
            iconName: "person" as const,
            onClick: openEdit
          }
        ]
      : task.author?.name
        ? [
            {
              text: task.author.name,
              label: "Author",
              iconName: "person" as const
            }
          ]
        : []),
    {
      text: teamsText || "None",
      label: "Teams",
      iconName: "assignment_ind" as const,
      onClick: openEdit,
      testId: "task-teams-metadata"
    },
    {
      text: visibilityText,
      label: "Visibility",
      iconName: task.visibility === TaskVisibilityValues.PRIVATE ? "lock" : "lock_open",
      onClick: openEdit,
      testId: "task-visibility-metadata"
    }
  ];

  return (
    <>
      <Breadcrumb title="Tasks" compactTitle="Task Details" backLink={{ to: "/tasks" }} />
      <ObjectHeading
        title={displayMentionText(task.name || "Untitled Task")}
        onTitleChange={canManageTasks && !nameHasMentions ? (name) => update({ name }) : undefined}
        iconName="inventory"
        tag={stageTag}
        metaData={metaData.length > 0 ? metaData : undefined}
        showFullMetadataInCondensed
        actions={headerActions}
      />
      {actions.error && (
        <div className="pt-4">
          <Banner variant="error" description={actions.error} inline hideSecondaryDismiss />
        </div>
      )}
      <DeleteTaskDialog
        open={showDeleteDialog}
        taskId={task.id}
        task={task}
        source="task_detail"
        onClose={() => setShowDeleteDialog(false)}
        onDeleted={() => navigate("/tasks")}
      />
      <EditTaskDialog
        open={showEditDialog}
        onClose={() => setShowEditDialog(false)}
        taskId={task.id}
        currentName={task.name || ""}
        currentAssignee={task.assignedTo ?? undefined}
        currentDueDate={task.dueDate}
        currentVisibility={task.visibility}
        task={task}
      />
    </>
  );
}
